import React, { FC, useEffect } from "react";

import { BaseComponentProps, SanitizeHtml, useThemeColors } from "@keepeek/refront-components";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";

import {
  EditoWidget as EditoWidgetModel,
  EditoWidgetMaxWidthContent,
  EditoWidgetPaddingX,
  EditoWidgetPaddingY,
} from "../../../models/configuration/components/editoWidget";
import { frontEditState } from "../../../providers/frontEdit/atoms/frontEdit";
import { useTextTemplating } from "../../../providers/textTemplating/hooks";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import { parseOptionsInternalUrl } from "../utils/parseInternalUrl";

export type EditoWidgetProps = {
  widgetId: string;
} & Omit<EditoWidgetModel, "id"> &
  BaseComponentProps &
  KWidgetProps;

const EditoWidget: FC<React.PropsWithChildren<EditoWidgetProps>> = function ({
  widgetId,
  backgroundColor = "transparent",
  content,
  editoWidgetMaxWidthContent,
  editoWidgetPaddingY,
  editoWidgetPaddingX,
  testId = "EditoWidget",
  type,
}) {
  const frontEdit = useRecoilValue(frontEditState);
  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  const { convertColor } = useThemeColors();

  const { loading, evaluate } = useTextTemplating();

  const { locale } = useRouter();
  // Calc current breakpoint pragmatically
  const { breakpoints } = useTheme();
  // I assume that if it does not match any other it is XS by default
  // const isXs = useMediaQuery(breakpoints.only("xs"));
  const isSm = useMediaQuery(breakpoints.only("sm"));
  const isMd = useMediaQuery(breakpoints.only("md"));
  const isLg = useMediaQuery(breakpoints.only("lg"));
  const isXl = useMediaQuery(breakpoints.only("xl"));
  let currentMediaQuery:
    | keyof EditoWidgetMaxWidthContent
    | keyof EditoWidgetPaddingY
    | keyof EditoWidgetPaddingX = "XS";
  if (isSm) {
    currentMediaQuery = "SM";
  }
  if (isMd) {
    currentMediaQuery = "MD";
  }
  if (isLg) {
    currentMediaQuery = "LG";
  }
  if (isXl) {
    currentMediaQuery = "XL";
  }
  if (frontEdit && currentMediaQuery !== "SM") {
    currentMediaQuery = "MD";
  }
  // Create breakpoint interpretation order
  const breakpointsOrder: Array<
    keyof EditoWidgetMaxWidthContent | keyof EditoWidgetPaddingY | keyof EditoWidgetPaddingX
  > = ["XS", "SM", "MD", "LG", "XL"];
  // Calc the queries left before the currentMediaQuery
  const mediaQueriesLeft = breakpointsOrder.filter(
    (_bpo, index) => index < breakpointsOrder.indexOf(currentMediaQuery),
  );
  // Calc the paddingY with the current media query or take the one just before
  let paddingY: string | undefined = editoWidgetPaddingY?.[currentMediaQuery];
  if (!paddingY && !!editoWidgetPaddingY) {
    mediaQueriesLeft.reverse().forEach((mq) => {
      if (!paddingY && editoWidgetPaddingY?.[mq]) {
        paddingY = editoWidgetPaddingY[mq];
      }
    });
  }
  // Calc the paddingX with the current media query or take the one just before
  let paddingX: string | undefined = editoWidgetPaddingX?.[currentMediaQuery];
  if (!paddingX && !!editoWidgetPaddingX) {
    mediaQueriesLeft.reverse().forEach((mq) => {
      if (!paddingX && editoWidgetPaddingX?.[mq]) {
        paddingX = editoWidgetPaddingX[mq];
      }
    });
  }
  // Calc the maxWidth with the current media query or take the one just before
  let maxWidth: string | undefined = editoWidgetMaxWidthContent?.[currentMediaQuery];
  if (!maxWidth && !!editoWidgetMaxWidthContent) {
    mediaQueriesLeft.reverse().forEach((mq) => {
      if (!maxWidth && editoWidgetMaxWidthContent?.[mq]) {
        maxWidth = editoWidgetMaxWidthContent[mq];
      }
    });
  }

  useEffect(() => {
    if (!content || !content.some((c) => c.language === locale)) {
      setWidgetState({ state: WidgetUIState.NULL });
    } else {
      setWidgetState({ state: WidgetUIState.RENDERED });
    }
  }, [content, locale, setWidgetState]);

  if (widgetState.state == WidgetUIState.RENDERED && !loading) {
    return (
      // BGColor for the whole block
      <Box
        data-testid={testId}
        style={{ backgroundColor: convertColor(backgroundColor), flex: 1 }}
        className="EditoWidget"
      >
        {/* Padding for content only */}
        <Box
          style={{
            paddingTop: paddingY,
            paddingBottom: paddingY,
            paddingLeft: paddingX,
            paddingRight: paddingX,
          }}
        >
          {/* Avoid calc padding in maxWidth for content */}
          <Box style={{ maxWidth }}>
            {
              <SanitizeHtml
                sanitizeOptions={{ allowedAttributes: false, allowedTags: false }}
                parseOptions={parseOptionsInternalUrl}
                html={evaluate(content?.find((c) => c.language === locale)?.value ?? "")}
              />
            }
          </Box>
        </Box>
      </Box>
    );
  }
  return null;
};

export default EditoWidget;
